<template>
    <div
        class="w-full sm:pt-4 justify-between items-start flex flex-col @container"
    >
        <LoadingState
            v-if="currentState === 'loading'"
            :loading="currentState === 'loading'"
        />
        <!-- ACTIVE ALERTS: -->
        <div
            v-if="currentState === 'alertsLoaded'"
            class="w-full grid gap-y-8 gap-x-6 grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3 @2xl:grid-cols-4 relative"
        >
            <!-- START SINGLE CARD -->
            <div v-for="(alert, i) in activeAlerts" :key="alert" class="w-full">
                <AlertItem class="" :key="`alert-${i}`" :alert="alert" />
            </div>
            <!-- INACTIVE ALERTS: -->
            <div
                v-if="currentState === 'alertsLoaded'"
                v-for="(alert, i) in inactiveAlerts"
                :key="alert"
                class="w-full"
            >
                <AlertItem class="" :key="`alert-${i}`" :alert="alert" />
            </div>
            <div
                class="@md:hidden @sm:col-span-2 sticky bottom-4 flex justify-center"
            >
                <CreateAlertButton :item="selectedProperty" />
            </div>
        </div>
        <div v-if="currentState === 'noResults'" class="mx-auto">
            <EmptyState
                class="w-full"
                :title="error ? error : 'No alerts match your search'"
                :description="emptyStateMessages.description"
                :action-label="emptyStateMessages.action"
                @action="handleClick"
            >
                <template #image>
                    <div
                        class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full justify-center items-center flex text-slate-600 dark:text-slate-300"
                    >
                        <Icon icon="alert" />
                    </div>
                </template>
            </EmptyState>
        </div>
    </div>
</template>

<script>
import { storeToRefs } from 'pinia';
import { ref, watch, computed } from 'vue';
import { useAlertsStore } from '@/store/alerts';
import AlertItem from '@/components/alerts/alert-item.vue';
import CreateAlertButton from '@/components/alerts/create-alert-button.vue';

import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useRouter } from 'vue-router';

export default {
    components: {
        AlertItem,
        CreateAlertButton,
        EmptyState,
        Icon,
        LoadingState,
    },
    setup() {
        const alertsStore = useAlertsStore();
        const router = useRouter();
        const {
            error,
            filteredAlerts,
            loading,
            activeAlert,
            selectedProperty,
            selectedGroups,
            selectedStatuses,
            selectedProperties,
        } = storeToRefs(alertsStore);

        const { setShowAlertModal, clearFilters, setAlertModalState } =
            alertsStore;

        const selectedAlert = computed(() =>
            activeAlert.value ? activeAlert.value : null
        );

        const activeAlerts = computed(() => {
            return filteredAlerts.value.filter(
                (alert) =>
                    alert.status === 'monitoring' || alert.status === 'found'
            );
        });

        const inactiveAlerts = computed(() => {
            return filteredAlerts.value.filter(
                (alert) =>
                    alert.status === 'expired' ||
                    alert.status === 'completed' ||
                    alert.status === 'unavailable'
            );
        });

        const emptyStateMessages = computed(() => {
            const messages = {};
            if (error.value) {
                messages['description'] = 'Please try again later.';
                messages['action'] = 'Hotels home';
            } else if (
                selectedGroups.value.length ||
                selectedStatuses.value.length ||
                selectedProperties.value.length
            ) {
                messages['description'] =
                    'No alerts found matching your filters.';
                messages['action'] = 'Clear filters';
            } else {
                messages['description'] =
                    'Create your first alert to track and be notified of award availability for your favorite hotels.';
                messages['action'] = 'Create Alert';
            }
            return messages;
        });

        const handleClick = () => {
            if (error.value) {
                router.push('/hotels');
            } else if (
                selectedGroups.value.length ||
                selectedStatuses.value.length ||
                selectedProperties.value.length
            ) {
                clearFilters();
            } else {
                setAlertModalState('step1.create');
                setShowAlertModal(true);
            }
        };

        const currentState = ref('loading');

        const states = {
            loading: {
                alertsLoaded: 'alertsLoaded',
                noResults: 'noResults',
                reload: 'loading',
            },
            noResults: {
                reload: 'loading',
                alertsLoaded: 'alertsLoaded',
            },
            alertsLoaded: {
                reload: 'loading',
                noResults: 'noResults',
                alertsLoaded: 'alertsLoaded',
            },
        };

        const transition = (action) => {
            const nextState = states[currentState.value][action];
            if (nextState) {
                currentState.value = nextState;
            } else {
                console.error(
                    `No transition for action ${action} from state ${currentState.value}`
                );
            }
        };

        watch(
            [() => loading.value, () => filteredAlerts.value],
            ([newLoading, newFilteredAlerts]) => {
                if (newLoading) {
                    transition('reload');
                } else if (newFilteredAlerts.length) {
                    transition('alertsLoaded');
                } else {
                    transition('noResults');
                }
            }
        );

        return {
            activeAlerts,
            emptyStateMessages,
            inactiveAlerts,
            handleClick,
            currentState,
            error,
            filteredAlerts,
            loading,
            selectedAlert,
            selectedProperty,
        };
    },
};
</script>
